// Dates, in one locale, chosen on purpose.
//
// `toLocaleString()` and `toLocaleString([])` both mean "whatever locale this
// browser happens to have". That is not a default — it is a different screen
// for every viewer: the same file listed as "Sep 02, 07:29 PM" for one person
// and "2 sept., 19:29" for another, in an interface that is otherwise entirely
// Romanian. Fourteen places did this.
//
// It stopped being cosmetic when the date became searchable: the picker indexed
// the rendered text, so "jul" found thirty-two files and "iulie" found none,
// and the person typing "iulie" concludes the filter is broken rather than that
// they were supposed to type English.
//
// Everything here takes an explicit locale. The structural test in
// __tests__/localeRules.test.js fails if any other file formats without one.

export const LOCALE = "ro-RO";

// Romanian does not use AM/PM. Stated rather than left to the locale's default,
// because a locale's default hour cycle can change between ICU versions.
const H24 = { hour: "2-digit", minute: "2-digit", hour12: false };

function parse(value) {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

const format = (value, options) => {
  const d = parse(value);
  return d ? d.toLocaleString(LOCALE, options) : "";
};

/** "2 sept., 19:29" — a row in a list. */
export const dateTime = (value) =>
  format(value, { day: "numeric", month: "short", ...H24 });

/** "2 sept. 2026" — anything older than the current week is worth a year. */
export const dateOnly = (value) =>
  format(value, { day: "numeric", month: "short", year: "numeric" });

/** "2 septembrie 2026, 19:29" — a detail panel, where there is room. */
export const dateTimeLong = (value) =>
  format(value, { day: "numeric", month: "long", year: "numeric", ...H24 });

/** "19:29" — beside a message, where the day is already known. */
export const timeOnly = (value) => format(value, H24);

/** "2 sept." — a badge, where a year would not fit. */
export const dayMonth = (value) =>
  format(value, { day: "numeric", month: "short" });

/**
 * Every Romanian spelling of a date, for a search box.
 *
 * The short form is what the row shows, so "sept." matches what the eye sees.
 * The long form is what a person types: somebody looking for July types
 * "iulie", not "iul.". Both, plus the numeric form, or the search is a quiz
 * about which abbreviation the formatter chose.
 *
 * Three entries and not four: a separate `{ month: "long" }` was in here until a
 * mutation went uncaught and showed it was already inside dateTimeLong. A line
 * no test can miss the absence of is a line that was doing nothing.
 */
export function searchableDate(value) {
  const d = parse(value);
  if (!d) return "";
  return [
    dateTime(d),          // "17 iul., 12:49" — what the row shows
    dateTimeLong(d),      // "17 iulie 2026 la 12:49" — and the word people type
    d.toLocaleString(LOCALE, { day: "2-digit", month: "2-digit", year: "numeric" }),
  ].join(" ");
}
